import React, { useEffect, useState } from "react";
import { Star, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api/client";

/**
 * Post-delivery order rating dialog. Use as a controlled modal:
 *   <OrderRatingDialog open={…} onClose={…} order={{id, order_no}} onRated={() => …} />
 */
const OrderRatingDialog = ({ open, onClose, order, onRated }) => {
    const [rating, setRating] = useState(5);
    const [hover, setHover] = useState(0);
    const [feedback, setFeedback] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setRating(5);
            setHover(0);
            setFeedback("");
        }
    }, [open]);

    if (!open || !order) return null;

    const submit = async () => {
        try {
            setBusy(true);
            await api.post(`/orders/${order.id}/rate`, { rating, feedback: feedback.trim() || null });
            toast.success("Thanks for your rating!");
            onRated?.();
            onClose?.();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not submit rating");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/50 backdrop-blur-sm p-4"
            data-testid="order-rating-dialog"
            onClick={onClose}
        >
            <div
                className="relative w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute right-4 top-4 rounded-full p-1.5 text-zinc-400 hover:bg-zinc-100"
                >
                    <X className="h-4 w-4" />
                </button>
                <p className="text-xs font-semibold uppercase tracking-wider text-brand-600">Rate order</p>
                <h3 className="mt-1 text-xl font-semibold text-zinc-900">How was {order.order_no}?</h3>
                <p className="mt-1 text-sm text-zinc-500">Your feedback helps us improve.</p>

                <div className="my-6 flex items-center justify-center gap-1.5" data-testid="rating-stars">
                    {[1, 2, 3, 4, 5].map((n) => {
                        const filled = n <= (hover || rating);
                        return (
                            <button
                                key={n}
                                onClick={() => setRating(n)}
                                onMouseEnter={() => setHover(n)}
                                onMouseLeave={() => setHover(0)}
                                className="rounded-full p-1 transition-transform hover:scale-110"
                                data-testid={`rating-star-${n}`}
                                aria-label={`${n} star${n > 1 ? "s" : ""}`}
                            >
                                <Star className={`h-7 w-7 ${filled ? "fill-amber-400 text-amber-400" : "text-zinc-300"}`} />
                            </button>
                        );
                    })}
                </div>

                <textarea
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="Tell us what went well (or what didn't)…"
                    rows={3}
                    maxLength={2000}
                    className="w-full rounded-xl border border-zinc-200 bg-white p-3 text-sm outline-none focus:border-brand-500"
                    data-testid="rating-feedback-input"
                />

                <div className="mt-4 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="rounded-full border border-zinc-200 px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
                        data-testid="rating-cancel-btn"
                    >
                        Maybe later
                    </button>
                    <button
                        onClick={submit}
                        disabled={busy}
                        className="rounded-full bg-brand-700 px-5 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50"
                        data-testid="rating-submit-btn"
                    >
                        {busy ? "Submitting…" : "Submit rating"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OrderRatingDialog;
